<template>
  <div class="recipe-page">
    <UserAppChrome back-mode="history" />

    <main class="recipe-shell">
      <header class="recipe-hero">
        <div class="recipe-hero__visual" aria-hidden="true">
          <img :src="recipeHeroImage" alt="" width="1600" height="1067" />
        </div>
        <div class="recipe-hero__copy">
          <p class="recipe-eyebrow">
            <span aria-hidden="true"></span>
            Chef digitale
          </p>
          <h1>Crea la tua<br><em>ricetta.</em></h1>
          <p>
            Parti dalle quantità del tuo piano e trasformale in un piatto nuovo,
            semplice e su misura per te.
          </p>
          <div class="recipe-hero__tags" aria-label="Caratteristiche">
            <span><i class="material-icons" aria-hidden="true">auto_awesome</i> Personalizzata</span>
            <span><i class="material-icons" aria-hidden="true">schedule</i> In pochi secondi</span>
            <span v-if="isLocalRecipeTest">Test locale · GPT-6 Luna</span>
          </div>
        </div>
      </header>

      <form class="recipe-form" @submit.prevent="submitForm">
        <div class="recipe-form__intro">
          <span class="recipe-form__icon material-icons" aria-hidden="true">restaurant_menu</span>
          <div>
            <p class="recipe-eyebrow recipe-eyebrow--dark">Componi il piatto</p>
            <h2>Inserisci le tue quantità</h2>
            <p>
              Indica un carboidrato, una proteina e un grasso seguendo le dosi previste dal tuo piano.
            </p>
          </div>
        </div>

        <section class="recipe-step" aria-labelledby="recipe-context-title">
          <div class="recipe-step__heading">
            <span>01</span>
            <div>
              <h3 id="recipe-context-title">Scegli il momento</h3>
              <p>Che tipo di ricetta vuoi preparare?</p>
            </div>
          </div>

          <div class="recipe-choice-grid">
            <label class="recipe-field">
              <span>Momento della giornata</span>
              <span class="recipe-control recipe-control--select">
                <i class="material-icons" aria-hidden="true">schedule</i>
                <select v-model="mealType" aria-label="Momento della giornata">
                  <option value="Colazione">Colazione</option>
                  <option value="Spuntino">Spuntino veloce</option>
                  <option value="Pranzo">Pranzo</option>
                  <option value="Cena">Cena</option>
                </select>
              </span>
            </label>

            <label class="recipe-field">
              <span>Preferenza</span>
              <span class="recipe-control recipe-control--select">
                <i class="material-icons" aria-hidden="true">tune</i>
                <select v-model="flavorType" aria-label="Preferenza dolce o salata">
                  <option value="Dolce">Dolce</option>
                  <option value="Salato">Salata</option>
                </select>
              </span>
            </label>
          </div>
        </section>

        <section class="recipe-step" aria-labelledby="recipe-ingredients-title">
          <div class="recipe-step__heading">
            <span>02</span>
            <div>
              <h3 id="recipe-ingredients-title">Aggiungi gli ingredienti</h3>
              <p>Scrivi l’alimento e seleziona la quantità.</p>
            </div>
          </div>

          <div class="ingredient-grid">
            <article class="ingredient-card ingredient-card--carbs">
              <header>
                <span class="material-icons" aria-hidden="true">bakery_dining</span>
                <div><small>Ingrediente 01</small><strong>Carboidrato</strong></div>
              </header>
              <label class="recipe-field">
                <span>Alimento</span>
                <input v-model="carbsIngredient" maxlength="100" :disabled="carbsGrams === 'Nessun carbo'" placeholder="Es. riso, pane, avena" aria-label="Ingrediente carboidrato" />
              </label>
              <label class="recipe-field">
                <span>Quantità</span>
                <span class="recipe-control recipe-control--select recipe-control--plain">
                  <select v-model="carbsGrams" aria-label="Quantità carboidrato">
                    <option disabled value="grammi">Seleziona</option>
                    <option value="Nessun carbo">Nessun carboidrato</option>
                    <option value="40 Gr">40 g</option><option value="50 Gr">50 g</option>
                    <option value="60 Gr">60 g</option><option value="70 Gr">70 g</option>
                    <option value="80 Gr">80 g</option><option value="90 Gr">90 g</option>
                    <option value="100 Gr">100 g</option><option value="120 Gr">120 g</option>
                    <option value="150 Gr">150 g</option>
                  </select>
                </span>
              </label>
              <p v-if="submitAttempted && carbsGrams === 'grammi'" class="field-error">Seleziona la quantità.</p>
            </article>

            <article class="ingredient-card ingredient-card--protein">
              <header>
                <span class="material-icons" aria-hidden="true">egg_alt</span>
                <div><small>Ingrediente 02</small><strong>Proteina</strong></div>
              </header>
              <label class="recipe-field">
                <span>Alimento</span>
                <input v-model="proteinIngredient" maxlength="100" placeholder="Es. pollo, uova, tofu" aria-label="Ingrediente proteico" />
              </label>
              <label class="recipe-field">
                <span>Quantità</span>
                <span class="recipe-control recipe-control--select recipe-control--plain">
                  <select v-model="proteinGrams" aria-label="Quantità proteina">
                    <option disabled value="grammi">Seleziona</option>
                    <option value="40 Gr">40 g</option><option value="50 Gr">50 g</option>
                    <option value="60 Gr">60 g</option><option value="70 Gr">70 g</option>
                    <option value="80 Gr">80 g</option><option value="90 Gr">90 g</option>
                    <option value="100 Gr">100 g</option><option value="120 Gr">120 g</option>
                    <option value="150 Gr">150 g</option><option value="180 Gr">180 g</option>
                    <option value="200 Gr">200 g</option>
                  </select>
                </span>
              </label>
              <p v-if="submitAttempted && proteinGrams === 'grammi'" class="field-error">Seleziona la quantità.</p>
            </article>

            <article class="ingredient-card ingredient-card--fat">
              <header>
                <span class="material-icons" aria-hidden="true">water_drop</span>
                <div><small>Ingrediente 03</small><strong>Grasso</strong></div>
              </header>
              <label class="recipe-field">
                <span>Alimento</span>
                <input v-model="fatIngredient" maxlength="100" placeholder="Es. olio, frutta secca" aria-label="Ingrediente grasso" />
              </label>
              <label class="recipe-field">
                <span>Quantità</span>
                <span class="recipe-control recipe-control--select recipe-control--plain">
                  <select v-model="fatGrams" aria-label="Quantità grasso">
                    <option disabled value="grammi">Seleziona</option>
                    <option value="5 Gr">5 g</option><option value="7 Gr">7 g</option>
                    <option value="10 Gr">10 g</option><option value="15 Gr">15 g</option>
                    <option value="20 Gr">20 g</option><option value="30 Gr">30 g</option>
                    <option value="40 Gr">40 g</option><option value="50 Gr">50 g</option>
                  </select>
                </span>
              </label>
              <p v-if="submitAttempted && fatGrams === 'grammi'" class="field-error">Seleziona la quantità.</p>
            </article>
          </div>
        </section>

        <section class="recipe-step" aria-labelledby="recipe-finish-title">
          <div class="recipe-step__heading">
            <span>03</span>
            <div>
              <h3 id="recipe-finish-title">Personalizza il risultato</h3>
              <p>Scegli lo stile e segnala ciò che vuoi evitare.</p>
            </div>
          </div>

          <div class="recipe-choice-grid">
            <label class="recipe-field">
              <span>Stile della ricetta</span>
              <span class="recipe-control recipe-control--select">
                <i class="material-icons" aria-hidden="true">public</i>
                <select v-model="recipeStyle" aria-label="Stile della ricetta">
                  <option disabled value="select">Seleziona uno stile</option>
                  <option>Mediterranea</option><option>Sud Americana</option>
                  <option>Spagnola</option><option>Asiatica</option>
                  <option>Nord Americana</option><option>Francese</option><option>Italiana</option>
                </select>
              </span>
            </label>

            <label class="recipe-field">
              <span>Ingredienti da escludere <small>facoltativo</small></span>
              <span class="recipe-control">
                <i class="material-icons" aria-hidden="true">block</i>
                <input v-model="ingredientsToExclude" maxlength="400" placeholder="Es. latte, pomodoro" />
              </span>
            </label>
          </div>
        </section>

        <div v-if="formError" class="recipe-alert recipe-alert--error" role="alert">
          <span class="material-icons" aria-hidden="true">error_outline</span>
          {{ formError }}
        </div>

        <div class="recipe-actions">
          <button class="recipe-submit" type="submit" :disabled="isLoading">
            <span class="material-icons" aria-hidden="true">{{ isLoading ? 'hourglass_top' : 'auto_awesome' }}</span>
            {{ isLoading ? 'Creazione in corso…' : 'Crea la mia ricetta' }}
            <span v-if="!isLoading" class="material-icons" aria-hidden="true">arrow_forward</span>
          </button>
          <router-link class="recipe-back" to="/user">Torna alla home</router-link>
        </div>
      </form>

      <section v-if="isLoading" class="recipe-loading" aria-live="polite">
        <span class="recipe-loading__spinner" aria-hidden="true"></span>
        <div><strong>Il nostro chef sta lavorando.</strong><p>Stiamo creando la tua ricetta personalizzata.</p></div>
      </section>

      <section v-if="generatedRecipe" class="generated-recipe" aria-labelledby="generated-recipe-title">
        <header>
          <span class="material-icons" aria-hidden="true">restaurant</span>
          <div>
            <p class="recipe-eyebrow recipe-eyebrow--dark">Ricetta pronta</p>
            <h2 id="generated-recipe-title">Buon appetito.</h2>
          </div>
        </header>
        <div class="formatted-recipe" v-html="formattedRecipe"></div>
      </section>
    </main>
  </div>
</template>
  
  
<script>
import { auth } from '@/firebaseConfig';
import { getEnvVar } from '@/lib/env';
import {
  createRecipePayload,
  formatRecipeText,
  isLocalRecipeEndpoint,
  requestRecipe,
  resolveRecipeFunctionUrl,
} from '@/lib/recipe-client';
import UserAppChrome from './UserAppChrome.vue';
import recipeHeroImage from '../../../fuscofit/assets/recipe-dashboard.jpg';

export default {
  components: { UserAppChrome },
  data() {
    const development = getEnvVar('NODE_ENV') === 'development' ||
      getEnvVar('MODE') === 'development' || getEnvVar('DEV') === true;
    const recipeFunctionUrl = resolveRecipeFunctionUrl({
      development,
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      override: getEnvVar('VITE_FN_GENERATE_RECIPE') || getEnvVar('VUE_APP_FN_GENERATE_RECIPE'),
    });
    return {
      recipeHeroImage,
      mealType: 'Pranzo',
      isLoading: false,
      generatedRecipe: null,
      ingredientsToExclude: '',
      flavorType: 'Salato',
      carbsIngredient: '',
      carbsGrams: 'grammi',
      proteinIngredient: '',
      proteinGrams: 'grammi',
      fatIngredient: '',
      fatGrams: 'grammi',
      recipeStyle: 'select',
      submitAttempted: false,
      formError: '',
      recipeFunctionUrl,
      isLocalRecipeTest: development && isLocalRecipeEndpoint(recipeFunctionUrl),
      requestController: null,
      hasUnmounted: false,
    };
  },
  beforeUnmount() {
    this.hasUnmounted = true;
    if (this.requestController) this.requestController.abort();
  },
  methods: {
    async submitForm() {
      if (this.isLoading || this.hasUnmounted) return;
      this.submitAttempted = true;
      this.formError = '';

      const validationErrors = this.validateForm();
      if (validationErrors.length) {
        this.formError = `Compila: ${validationErrors.join(', ')}`;
        return;
      }

      this.isLoading = true;
      this.generatedRecipe = null;
      this.requestController = new AbortController();
      try {
        const recipe = await this.getRecipe();
        if (!this.hasUnmounted) this.generatedRecipe = recipe;
      } catch (error) {
        if (!this.hasUnmounted) {
          this.formError = error.message || 'Si è verificato un errore. Riprova.';
        }
      } finally {
        if (!this.hasUnmounted) {
          this.isLoading = false;
          this.requestController = null;
        }
      }
    },

    validateForm() {
      const missing = [];
      if (this.carbsGrams !== 'Nessun carbo' && !this.carbsIngredient.trim()) missing.push('carboidrato');
      if (!this.proteinIngredient.trim()) missing.push('proteina');
      if (!this.fatIngredient.trim()) missing.push('grasso');
      if (this.carbsGrams === 'grammi') missing.push('grammi carbo');
      if (this.proteinGrams === 'grammi') missing.push('grammi proteine');
      if (this.fatGrams === 'grammi') missing.push('grammi grassi');
      if (this.recipeStyle === 'select') missing.push('stile ricetta');
      return missing;
    },

    async getRecipe() {
      return requestRecipe({
        endpoint: this.recipeFunctionUrl,
        payload: createRecipePayload(this),
        user: auth.currentUser,
        signal: this.requestController.signal,
      });
    },
  },

  computed: {
    formattedRecipe() {
      return formatRecipeText(this.generatedRecipe);
    },
  },
};
</script>
  
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Montserrat:ital,wght@0,700;0,800;0,900;1,900&display=swap');

.recipe-page {
  --recipe-black: #111110;
  --recipe-red: #d50b35;
  --recipe-bg: #f5f2ee;
  --recipe-surface: rgba(255, 255, 255, 0.9);
  --recipe-border: rgba(17, 17, 16, 0.1);
  min-height: 100vh;
  background:
    radial-gradient(circle at 82% 4%, rgba(213, 11, 53, 0.07), transparent 31%),
    linear-gradient(180deg, #f8f6f3 0%, var(--recipe-bg) 100%);
  color: var(--recipe-black);
  font-family: 'DM Sans', sans-serif;
}

.recipe-page,
.recipe-page *,
.recipe-page *::before,
.recipe-page *::after {
  box-sizing: border-box;
}

.recipe-shell {
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: clamp(18px, 3.5vw, 42px) 0 150px;
}

.recipe-hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
  min-height: 370px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 28px;
  background: var(--recipe-black);
  box-shadow: 0 26px 70px rgba(17, 17, 16, 0.18);
  isolation: isolate;
}

.recipe-hero::after {
  position: absolute;
  z-index: 1;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(10, 10, 9, 0.98) 0%, rgba(10, 10, 9, 0.88) 46%, rgba(10, 10, 9, 0.08) 78%),
    linear-gradient(0deg, rgba(10, 10, 9, 0.72), transparent 65%);
  content: '';
  pointer-events: none;
}

.recipe-hero__visual {
  position: absolute;
  z-index: 0;
  inset: 0 0 0 34%;
}

.recipe-hero__visual img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center 54%;
  filter: saturate(0.82) contrast(1.05);
}

.recipe-hero__copy {
  position: relative;
  z-index: 2;
  align-self: center;
  max-width: 670px;
  padding: clamp(32px, 5vw, 64px);
  color: #fff;
}

.recipe-eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 14px;
  color: #f3869d;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  line-height: 1.3;
  text-transform: uppercase;
}

.recipe-eyebrow > span {
  width: 32px;
  height: 3px;
  flex: 0 0 auto;
  background: var(--recipe-red);
}

.recipe-eyebrow--dark {
  margin-bottom: 7px;
  color: var(--recipe-red);
}

.recipe-hero h1 {
  max-width: 650px;
  margin: 0;
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(2.8rem, 7vw, 5rem);
  font-weight: 900;
  letter-spacing: -0.075em;
  line-height: 0.88;
  text-transform: uppercase;
}

.recipe-hero h1 em {
  color: var(--recipe-red);
  font-style: italic;
}

.recipe-hero__copy > p:not(.recipe-eyebrow) {
  max-width: 520px;
  margin: 24px 0 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: clamp(0.96rem, 2vw, 1.1rem);
  line-height: 1.55;
}

.recipe-hero__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 26px;
}

.recipe-hero__tags span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.86);
  font-size: 0.73rem;
  font-weight: 700;
}

.recipe-hero__tags .material-icons {
  color: #ff718f;
  font-size: 16px;
}

.recipe-form,
.generated-recipe {
  margin-top: 24px;
  padding: clamp(24px, 4.2vw, 50px);
  border: 1px solid rgba(255, 255, 255, 0.86);
  border-radius: 26px;
  background: var(--recipe-surface);
  box-shadow:
    0 20px 55px rgba(17, 17, 16, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}

.recipe-form__intro,
.generated-recipe > header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 18px;
  align-items: center;
}

.recipe-form__icon,
.generated-recipe > header > .material-icons {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border: 1px solid rgba(213, 11, 53, 0.16);
  border-radius: 18px;
  background: linear-gradient(145deg, rgba(213, 11, 53, 0.14), rgba(213, 11, 53, 0.045));
  color: var(--recipe-red);
  font-size: 27px;
  box-shadow: 0 12px 28px rgba(213, 11, 53, 0.09);
}

.recipe-form__intro h2,
.generated-recipe h2 {
  margin: 0;
  color: var(--recipe-black);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(1.55rem, 3.5vw, 2.25rem);
  font-weight: 800;
  letter-spacing: -0.05em;
  line-height: 1;
}

.recipe-form__intro > div > p:last-child {
  max-width: 660px;
  margin: 10px 0 0;
  color: #67655f;
  line-height: 1.5;
}

.recipe-step {
  margin-top: 36px;
  padding-top: 32px;
  border-top: 1px solid var(--recipe-border);
}

.recipe-step__heading {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}

.recipe-step__heading > span {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: 13px;
  background: var(--recipe-black);
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
}

.recipe-step__heading h3 {
  margin: 0;
  color: var(--recipe-black);
  font-family: 'Montserrat', sans-serif;
  font-size: 1.08rem;
  font-weight: 800;
  letter-spacing: -0.025em;
}

.recipe-step__heading p {
  margin: 4px 0 0;
  color: #77746e;
  font-size: 0.84rem;
}

.recipe-choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.recipe-field {
  display: grid;
  min-width: 0;
  gap: 8px;
}

.recipe-field > span:first-child {
  color: #56544f;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.recipe-field > span:first-child small {
  color: #99958d;
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.recipe-control {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
}

.recipe-control > .material-icons {
  position: absolute;
  z-index: 1;
  left: 15px;
  color: var(--recipe-red);
  font-size: 19px;
  pointer-events: none;
}

.recipe-control--select::after {
  position: absolute;
  right: 16px;
  width: 8px;
  height: 8px;
  border-right: 2px solid #77746e;
  border-bottom: 2px solid #77746e;
  content: '';
  pointer-events: none;
  transform: translateY(-3px) rotate(45deg);
}

.recipe-field > input,
.recipe-control input,
.recipe-control select {
  width: 100%;
  min-width: 0;
  height: 52px;
  margin: 0;
  border: 1px solid rgba(17, 17, 16, 0.11);
  border-radius: 14px;
  outline: none;
  background: #f7f5f2;
  color: var(--recipe-black);
  font-family: 'DM Sans', sans-serif;
  font-size: 0.94rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.recipe-field > input {
  padding: 0 15px;
}

.recipe-control input,
.recipe-control select {
  padding: 0 44px;
}

.recipe-control select {
  padding-right: 42px;
  appearance: none;
  -webkit-appearance: none;
}

.recipe-control--plain select {
  padding-left: 14px;
}

.recipe-field input::placeholder {
  color: #9b9891;
}

.recipe-field > input:focus,
.recipe-control input:focus,
.recipe-control select:focus {
  border-color: rgba(213, 11, 53, 0.6);
  background: #fff;
  box-shadow: 0 0 0 4px rgba(213, 11, 53, 0.08);
}

.ingredient-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.ingredient-card {
  position: relative;
  min-width: 0;
  padding: 18px;
  overflow: hidden;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 20px;
  background:
    radial-gradient(circle at 100% 0, rgba(var(--ingredient-rgb), 0.1), transparent 42%),
    linear-gradient(145deg, #fff, #f8f6f3);
}

.ingredient-card::after {
  position: absolute;
  top: 18px;
  right: 0;
  bottom: 18px;
  width: 3px;
  border-radius: 999px 0 0 999px;
  background: rgb(var(--ingredient-rgb));
  content: '';
}

.ingredient-card--carbs { --ingredient-rgb: 213, 11, 53; }
.ingredient-card--protein { --ingredient-rgb: 225, 99, 55; }
.ingredient-card--fat { --ingredient-rgb: 190, 143, 33; }

.ingredient-card > header {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 18px;
}

.ingredient-card > header > .material-icons {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  border-radius: 13px;
  background: rgba(var(--ingredient-rgb), 0.11);
  color: rgb(var(--ingredient-rgb));
  font-size: 21px;
}

.ingredient-card header div {
  display: grid;
  gap: 2px;
}

.ingredient-card header small {
  color: #8b8881;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.ingredient-card header strong {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.96rem;
  font-weight: 800;
}

.ingredient-card .recipe-field + .recipe-field {
  margin-top: 13px;
}

.field-error {
  margin: 7px 0 0;
  color: #b20b2d;
  font-size: 0.72rem;
  font-weight: 700;
}

.recipe-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 22px;
  padding: 13px 15px;
  border-radius: 14px;
  font-size: 0.86rem;
  font-weight: 600;
}

.recipe-alert--error {
  border: 1px solid rgba(178, 11, 45, 0.18);
  background: rgba(213, 11, 53, 0.07);
  color: #9e0a29;
}

.recipe-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 30px;
}

.recipe-submit,
.recipe-back {
  min-height: 54px;
  border-radius: 999px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-decoration: none;
  text-transform: uppercase;
}

.recipe-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 260px;
  padding: 13px 21px;
  border: 0;
  background: var(--recipe-red);
  color: #fff;
  box-shadow: 0 14px 28px rgba(213, 11, 53, 0.22);
  cursor: pointer;
}

.recipe-submit:hover:not(:disabled) {
  background: #b6082a;
  transform: translateY(-1px);
}

.recipe-submit:disabled {
  opacity: 0.62;
  cursor: wait;
}

.recipe-submit .material-icons {
  font-size: 19px;
}

.recipe-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 13px 20px;
  border: 1px solid var(--recipe-border);
  color: #5f5c56;
}

.recipe-loading {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 18px;
  padding: 20px 24px;
  border-radius: 18px;
  background: var(--recipe-black);
  color: #fff;
  box-shadow: 0 18px 38px rgba(17, 17, 16, 0.14);
}

.recipe-loading__spinner {
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border: 3px solid rgba(255, 255, 255, 0.22);
  border-top-color: var(--recipe-red);
  border-radius: 50%;
  animation: recipe-spin 0.8s linear infinite;
}

.recipe-loading strong {
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95rem;
}

.recipe-loading p {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.82rem;
}

.generated-recipe > header {
  padding-bottom: 22px;
  border-bottom: 1px solid var(--recipe-border);
}

.formatted-recipe {
  margin-top: 22px;
  color: #3f3d39;
  font-size: 1rem;
  line-height: 1.75;
}

.formatted-recipe :deep(strong) {
  color: var(--recipe-black);
  font-family: 'Montserrat', sans-serif;
  font-size: 1.05rem;
}

@keyframes recipe-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 900px) {
  .ingredient-grid {
    grid-template-columns: 1fr;
  }

  .ingredient-card {
    display: grid;
    grid-template-columns: minmax(170px, 0.8fr) repeat(2, minmax(0, 1fr));
    gap: 14px;
    align-items: end;
  }

  .ingredient-card > header {
    align-self: center;
    margin: 0;
  }

  .ingredient-card .recipe-field + .recipe-field {
    margin-top: 0;
  }
}

@media (max-width: 700px) {
  .recipe-shell {
    width: calc(100% - 24px);
    padding-top: 12px;
    padding-bottom: 138px;
  }

  .recipe-hero {
    grid-template-columns: 1fr;
    min-height: 390px;
    border-radius: 21px;
  }

  .recipe-hero::after {
    background:
      linear-gradient(0deg, rgba(10, 10, 9, 0.98) 0%, rgba(10, 10, 9, 0.79) 56%, rgba(10, 10, 9, 0.18) 100%),
      linear-gradient(90deg, rgba(10, 10, 9, 0.42), transparent);
  }

  .recipe-hero__visual {
    inset: 0;
  }

  .recipe-hero__visual img {
    object-position: center 44%;
  }

  .recipe-hero__copy {
    align-self: end;
    padding: 25px 22px;
  }

  .recipe-eyebrow {
    margin-bottom: 10px;
    font-size: 0.67rem;
  }

  .recipe-hero h1 {
    max-width: 330px;
    font-size: clamp(2.45rem, 13vw, 3.45rem);
  }

  .recipe-hero__copy > p:not(.recipe-eyebrow) {
    max-width: 360px;
    margin-top: 16px;
    font-size: 0.9rem;
    line-height: 1.42;
  }

  .recipe-hero__tags {
    margin-top: 18px;
  }

  .recipe-form,
  .generated-recipe {
    margin-top: 14px;
    padding: 20px 17px;
    border-radius: 20px;
  }

  .recipe-form__intro {
    gap: 13px;
  }

  .recipe-form__icon,
  .generated-recipe > header > .material-icons {
    width: 48px;
    height: 48px;
    border-radius: 15px;
    font-size: 23px;
  }

  .recipe-form__intro h2,
  .generated-recipe h2 {
    font-size: 1.35rem;
  }

  .recipe-form__intro > div > p:last-child {
    grid-column: 1 / -1;
    margin-top: 7px;
    font-size: 0.83rem;
    line-height: 1.42;
  }

  .recipe-step {
    margin-top: 27px;
    padding-top: 24px;
  }

  .recipe-step__heading {
    margin-bottom: 16px;
  }

  .recipe-choice-grid,
  .ingredient-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .ingredient-card {
    display: block;
    padding: 16px;
    border-radius: 18px;
  }

  .ingredient-card > header {
    margin-bottom: 15px;
  }

  .ingredient-card .recipe-field + .recipe-field {
    margin-top: 12px;
  }

  .recipe-field > input,
  .recipe-control input,
  .recipe-control select {
    height: 50px;
    font-size: 1rem;
  }

  .recipe-actions {
    display: grid;
    gap: 9px;
    margin-top: 24px;
  }

  .recipe-submit,
  .recipe-back {
    width: 100%;
    min-width: 0;
  }

  .recipe-back {
    min-height: 48px;
  }

  .recipe-loading {
    padding: 17px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .recipe-loading__spinner {
    animation: none;
  }

  .recipe-submit {
    transition: none;
  }
}
</style>
